import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Grid, CircularProgress, Box, Typography } from '@mui/material';
import { useApiGet, goClient, formGoClient } from '../../../utils/httpClientTs';
import {
  getIRSFormQuestions,
  getIRSSectionMetadata,
} from '../../../endpoints/irsForms';
import { IRSQuestion, IRSSectionMetadata } from '../../../types/irsForms';
import IRSFormQuestion from './IRSFormQuestion';
import IRSFormRepeatingGroup from './IRSFormRepeatingGroup';

// Simple debounce function
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

interface IRSFormSectionProps {
  formId: string;
  sectionId: string;
  editable: boolean;
  formType: 'lc' | 'sc' | 'rg';
  isActive?: boolean;
}

const IRSFormSection: React.FC<IRSFormSectionProps> = ({
  formId,
  sectionId,
  editable,
  formType,
  isActive = false,
}) => {
  const [questions, setQuestions] = useState<IRSQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [saveStatus, setSaveStatus] = useState<
    Record<string, 'idle' | 'saving' | 'saved' | 'error'>
  >({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const pendingSavesRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const savedTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  const {
    data: questionsData,
    isLoading,
    isFetching,
    refetch,
  } = useApiGet(
    ['irs-form-questions', formId, sectionId],
    () => getIRSFormQuestions(formId, sectionId),
    {
      enabled: !!formId && !!sectionId && isActive, // Only fetch when tab is active
      retry: 1,
    }
  );

  // Special section ID that requires metadata comparison
  const SPECIAL_SECTION_ID = '3f7dc6636fd041d8baa973f234d27955';

  // Fetch metadata for special section
  const { data: sectionMetadata } = useApiGet<IRSSectionMetadata>(
    ['irs-section-metadata', formId, sectionId],
    () => getIRSSectionMetadata(formId, sectionId),
    {
      enabled:
        !!formId && !!sectionId && sectionId === SPECIAL_SECTION_ID && isActive,
      retry: 1,
    }
  );

  // Refetch when tab becomes active to ensure fresh data
  useEffect(() => {
    if (isActive && formId && sectionId) {
      refetch();
    }
  }, [isActive, formId, sectionId, refetch]);

  // Re-initialize answers whenever questionsData changes (e.g., after refetch)
  // This ensures fresh data is displayed when switching back to a tab
  useEffect(() => {
    if (questionsData && isActive) {
      setQuestions(questionsData);

      // Always re-initialize answers when questionsData changes
      const initialAnswers: Record<string, any> = {};
      questionsData.forEach((q) => {
        if (q.decimal_answer !== null && q.decimal_answer !== undefined) {
          initialAnswers[q.question_id] = q.decimal_answer;
        } else if (
          q.integer_answer !== null &&
          q.integer_answer !== undefined
        ) {
          initialAnswers[q.question_id] = q.integer_answer;
        } else if (q.text_answer !== null && q.text_answer !== undefined) {
          initialAnswers[q.question_id] = q.text_answer;
        } else if (
          q.boolean_answer !== null &&
          q.boolean_answer !== undefined
        ) {
          initialAnswers[q.question_id] = q.boolean_answer;
        } else if (q.date_answer) {
          initialAnswers[q.question_id] = q.date_answer;
        } else if (q.document_id) {
          initialAnswers[q.question_id] = q.document_id;
        } else {
          initialAnswers[q.question_id] = null;
        }
      });

      setAnswers(initialAnswers);
    }
  }, [questionsData, isActive, sectionId, sectionMetadata]);

  // Debounced save function
  const debouncedSaveRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Save function with comprehensive state management
  const performSave = useCallback(
    async (
      questionId: string,
      value: any,
      answerType: string,
      signal?: AbortSignal,
      answerGroupId?: string
    ) => {
      // Use composite key for repeating groups, simple key for regular questions
      const statusKey = answerGroupId
        ? `${questionId}_${answerGroupId}`
        : questionId;

      // Cancel any existing timeout for this question
      if (savedTimeoutsRef.current[statusKey]) {
        clearTimeout(savedTimeoutsRef.current[statusKey]);
        delete savedTimeoutsRef.current[statusKey];
      }

      // Set status to saving
      setSaveStatus((prev) => ({ ...prev, [statusKey]: 'saving' }));
      setSaveErrors((prev) => {
        const updated = { ...prev };
        delete updated[statusKey];
        return updated;
      });
      pendingSavesRef.current.add(statusKey);

      try {
        // Handle image type with file upload
        if (answerType === 'image' && value instanceof File) {
          // Upload file using FormData
          const formData = new FormData();
          formData.append('file', value);
          formData.append('document_type', 'supporting_document');

          const response = await formGoClient.put<{
            success: boolean;
            message: string;
            sub_totals?: Record<string, number>;
          }>(
            `/api/annual-reports/irs/${formId}/questions/${questionId}/answer`,
            formData,
            {
              signal,
            }
          );

          // Update sub_totals in the UI if provided
          if (response.data.sub_totals) {
            // Update questions with new sub_total values
            setQuestions((prevQuestions) => {
              return prevQuestions.map((q) => {
                if (
                  q.answer_type === 'sub_total' &&
                  response.data.sub_totals &&
                  response.data.sub_totals[q.question_id] !== undefined
                ) {
                  return {
                    ...q,
                    sub_total_value: response.data.sub_totals[q.question_id],
                  };
                }
                return q;
              });
            });
          }
        } else {
          // Regular answer update for other types
          const updateData: any = {};
          if (answerType === 'decimal') {
            updateData.decimal_answer =
              value !== null && value !== '' ? parseFloat(String(value)) : null;
          } else if (answerType === 'integer') {
            // Parse as float first to handle "1.00" correctly, then floor to integer
            // This ensures "1.00" becomes 1, not 100
            updateData.integer_answer =
              value !== null && value !== ''
                ? Math.floor(parseFloat(String(value)))
                : null;
          } else if (answerType === 'text') {
            updateData.text_answer = value || null;
          } else if (answerType === 'boolean') {
            updateData.boolean_answer = value;
          } else if (answerType === 'date') {
            updateData.date_answer = value || null;
          } else if (answerType === 'image') {
            // For image type, value is document_id (string) or null
            updateData.document_id = value || null;
          }

          // Include answer_group_id if provided (for repeating groups)
          if (answerGroupId) {
            updateData.answer_group_id = answerGroupId;
          }

          // Make API call with signal support
          const response = await goClient.put<{
            success: boolean;
            message: string;
            sub_totals?: Record<string, number>;
          }>(
            `/api/annual-reports/irs/${formId}/questions/${questionId}/answer`,
            updateData,
            {
              signal,
            }
          );

          // Update sub_totals in the UI if provided
          if (response.data.sub_totals) {
            // Update questions with new sub_total values
            setQuestions((prevQuestions) => {
              return prevQuestions.map((q) => {
                if (
                  q.answer_type === 'sub_total' &&
                  response.data.sub_totals &&
                  response.data.sub_totals[q.question_id] !== undefined
                ) {
                  return {
                    ...q,
                    sub_total_value: response.data.sub_totals[q.question_id],
                  };
                }
                return q;
              });
            });
          }
        }

        // Success: set status to saved, remove from pending
        setSaveStatus((prev) => ({ ...prev, [statusKey]: 'saved' }));
        pendingSavesRef.current.delete(statusKey);

        // Auto-transition saved to idle after 2 seconds
        savedTimeoutsRef.current[statusKey] = setTimeout(() => {
          setSaveStatus((prev) => {
            const updated = { ...prev };
            if (updated[statusKey] === 'saved') {
              updated[statusKey] = 'idle';
            }
            return updated;
          });
          delete savedTimeoutsRef.current[statusKey];
        }, 2000);
      } catch (error: any) {
        // Check if error is due to abort
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          // Request was cancelled, don't update status
          return;
        }

        // Error: set status to error, store error message, remove from pending
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save answer. Please try again.';
        setSaveStatus((prev) => ({ ...prev, [statusKey]: 'error' }));
        setSaveErrors((prev) => ({ ...prev, [statusKey]: errorMessage }));
        pendingSavesRef.current.delete(statusKey);
        console.error('Failed to save answer:', error);
      }
    },
    [formId]
  );

  useEffect(() => {
    debouncedSaveRef.current = debounce(
      async (
        questionId: string,
        value: any,
        answerType: string,
        answerGroupId?: string
      ) => {
        // Use composite key for repeating groups, simple key for regular questions
        const controllerKey = answerGroupId
          ? `${questionId}_${answerGroupId}`
          : questionId;

        // Cancel any existing request for this question
        if (abortControllersRef.current[controllerKey]) {
          abortControllersRef.current[controllerKey].abort();
        }

        // Create new AbortController for this save
        const abortController = new AbortController();
        abortControllersRef.current[controllerKey] = abortController;

        await performSave(
          questionId,
          value,
          answerType,
          abortController.signal,
          answerGroupId
        );
      },
      800
    );

    return () => {
      // Cleanup on unmount: cancel all pending requests
      // Capture ref values to avoid stale closure issues
      const abortControllers = abortControllersRef.current;
      const savedTimeouts = savedTimeoutsRef.current;
      const pendingSaves = pendingSavesRef.current;

      Object.values(abortControllers).forEach((controller) => {
        controller.abort();
      });
      Object.values(savedTimeouts).forEach((timeout) => {
        clearTimeout(timeout);
      });
      // Clear the captured Set
      pendingSaves.clear();
      // Reset refs
      abortControllersRef.current = {};
      savedTimeoutsRef.current = {};
      pendingSavesRef.current = new Set();
    };
  }, [performSave]);

  const handleAnswerChange = (
    questionId: string,
    value: any,
    answerType: string,
    answerGroupId?: string
  ) => {
    // Optimistic UI update - update immediately
    // For repeating groups, use a composite key
    const answerKey = answerGroupId
      ? `${questionId}_${answerGroupId}`
      : questionId;
    setAnswers((prev) => ({ ...prev, [answerKey]: value }));
    if (editable && debouncedSaveRef.current) {
      debouncedSaveRef.current(questionId, value, answerType, answerGroupId);
    }
  };

  // Retry save function
  const retrySave = useCallback(
    (questionIdOrKey: string) => {
      // Extract questionId and answerGroupId from composite key if present
      let questionId: string;
      let answerGroupId: string | undefined;

      if (questionIdOrKey.includes('_')) {
        // Composite key format: questionId_answerGroupId
        const parts = questionIdOrKey.split('_');
        // Handle case where questionId itself might contain underscores
        // We know answerGroupId is a UUID without hyphens (32 hex chars)
        // So we can split from the end
        if (parts.length >= 2) {
          // Take the last 32 characters as answerGroupId, rest as questionId
          const lastPart = parts[parts.length - 1];
          if (lastPart.length === 32) {
            // Likely an answerGroupId (32 hex chars)
            answerGroupId = lastPart;
            questionId = parts.slice(0, -1).join('_');
          } else {
            // Not a composite key, use as-is
            questionId = questionIdOrKey;
          }
        } else {
          questionId = questionIdOrKey;
        }
      } else {
        questionId = questionIdOrKey;
      }

      const question = questions.find((q) => q.question_id === questionId);
      if (!question) return;

      // Use the original key to get the answer value
      const value = answers[questionIdOrKey];
      // Clear error state before retry
      const statusKey = answerGroupId
        ? `${questionId}_${answerGroupId}`
        : questionId;
      setSaveErrors((prev) => {
        const updated = { ...prev };
        delete updated[statusKey];
        return updated;
      });
      // Cancel any existing request for this question
      if (abortControllersRef.current[statusKey]) {
        abortControllersRef.current[statusKey].abort();
      }
      // Create new AbortController for retry
      const abortController = new AbortController();
      abortControllersRef.current[statusKey] = abortController;
      // Trigger save immediately (bypass debounce for retry)
      if (editable) {
        performSave(
          questionId,
          value,
          question.answer_type,
          abortController.signal,
          answerGroupId
        );
      }
    },
    [questions, answers, editable, performSave]
  );

  const handleOtherItemsUpdate = () => {
    // Only refetch when explicitly needed (e.g., other_line_items update)
    refetch();
  };

  // Navigation protection - warn if pending saves exist
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (pendingSavesRef.current.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Group questions by parent/child relationships
  const getQuestionGroups = () => {
    const parentQuestions: IRSQuestion[] = [];
    const childQuestionsMap: Record<string, IRSQuestion[]> = {};

    questions.forEach((q) => {
      if (!q.parent_question_id) {
        parentQuestions.push(q);
      } else {
        if (!childQuestionsMap[q.parent_question_id]) {
          childQuestionsMap[q.parent_question_id] = [];
        }
        childQuestionsMap[q.parent_question_id].push(q);
      }
    });

    // Sort parent questions by display_order
    parentQuestions.sort((a, b) => {
      const orderA = a.display_order || 0;
      const orderB = b.display_order || 0;
      return orderA - orderB;
    });

    // Sort child questions by display_order
    Object.keys(childQuestionsMap).forEach((parentId) => {
      childQuestionsMap[parentId].sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });
    });

    return { parentQuestions, childQuestionsMap };
  };

  // Build a map of questions that are dependent on sub_totals (for indentation)
  const dependentQuestionIds = useMemo(() => {
    const dependentSet = new Set<string>();
    questions.forEach((q) => {
      if (q.answer_type === 'sub_total' && q.dependent_question_ids) {
        q.dependent_question_ids.forEach((id) => dependentSet.add(id));
      }
    });
    return dependentSet;
  }, [questions]);

  const renderQuestionWithChildren = (
    question: IRSQuestion,
    parentAnswer: any,
    childQuestionsMap: Record<string, IRSQuestion[]>,
    depth: number = 0
  ) => {
    const childQuestions = childQuestionsMap[question.question_id] || [];
    const questionAnswer = answers[question.question_id];

    // Check if this question is dependent on a sub_total or has a parent (for indentation)
    const isDependentOnSubTotal = dependentQuestionIds.has(
      question.question_id
    );
    const hasParent = !!question.parent_question_id;
    const isIndented = isDependentOnSubTotal || hasParent;

    // Build element to return (question + children; no separator/line break before sub_total)
    let elementToReturn: React.ReactNode;

    if (question.supports_repeating_groups) {
      elementToReturn = (
        <Grid item xs={12} key={question.question_id}>
          <IRSFormRepeatingGroup
            parentQuestion={question}
            formId={formId}
            editable={editable}
            onAnswerChange={handleAnswerChange}
            onOtherItemsUpdate={handleOtherItemsUpdate}
            saveStatus={saveStatus}
            saveErrors={saveErrors}
            onRetrySave={retrySave}
            answers={answers}
            childQuestionsMap={childQuestionsMap}
          />
        </Grid>
      );
    } else {
      elementToReturn = (
        <Grid item xs={12} key={question.question_id}>
          <IRSFormQuestion
            question={question}
            answer={questionAnswer}
            onChange={(value) =>
              handleAnswerChange(
                question.question_id,
                value,
                question.answer_type
              )
            }
            disabled={
              !editable ||
              saveStatus[question.question_id] === 'saving' ||
              question.answer_type === 'sub_total' // Sub_totals are always read-only
            }
            parentAnswer={parentAnswer}
            formId={formId}
            onOtherItemsUpdate={handleOtherItemsUpdate}
            saveStatus={saveStatus[question.question_id] || 'idle'}
            saveError={saveErrors[question.question_id]}
            onRetrySave={() => retrySave(question.question_id)}
            isIndented={isIndented}
          />
          {/* Recursively render nested children */}
          {childQuestions.map((childQuestion) =>
            renderQuestionWithChildren(
              childQuestion,
              questionAnswer, // Pass this question's answer as parentAnswer for its children
              childQuestionsMap,
              depth + 1
            )
          )}
        </Grid>
      );
    }

    return elementToReturn;
  };

  const { parentQuestions, childQuestionsMap } = getQuestionGroups();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Show a subtle loading overlay when refetching (but we have data)
  const showRefetchLoading = isFetching && !isLoading && questions.length > 0;

  return (
    <Box sx={{ position: 'relative', paddingBottom: '100px' }}>
      {showRefetchLoading && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
            zIndex: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Refreshing...
          </Typography>
        </Box>
      )}
      <Grid container spacing={2}>
        {parentQuestions.flatMap((question) => {
          const parentAnswer = answers[question.question_id];

          // renderQuestionWithChildren may return a single element or an array (includes separator if needed)
          const questionResult = renderQuestionWithChildren(
            question,
            parentAnswer,
            childQuestionsMap,
            0
          );

          // Normalize to array
          const questionElements = Array.isArray(questionResult)
            ? questionResult
            : [questionResult];

          return questionElements;
        })}
      </Grid>
    </Box>
  );
};

export default IRSFormSection;
